import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from 'sonner';
import {
  Search,
  Plus,
  FileSearch,
  Calendar,
  Users,
  Trash2,
  Archive,
  Eye,
  RefreshCw,
  Filter,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  GitCompare
} from 'lucide-react';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatMoney = (amount) => {
  if (!amount && amount !== 0) return '$0';
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const ESTADO_CONFIG = {
  en_proceso: { label: 'En Proceso', color: 'bg-blue-100 text-blue-700', icon: Clock },
  completada: { label: 'Completada', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  archivada: { label: 'Archivada', color: 'bg-slate-100 text-slate-700', icon: Archive }
};

const InvestigacionesList = () => {
  const navigate = useNavigate();
  const [investigations, setInvestigations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, titulo: '' });

  useEffect(() => {
    loadInvestigations();
  }, [filtroEstado]);

  const loadInvestigations = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroEstado !== 'all') {
        params.estado = filtroEstado;
      }
      const res = await api.get('/investigations', { params });
      setInvestigations(res.data.investigations || []);
    } catch (error) {
      toast.error('Error al cargar investigaciones');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/investigations/${deleteDialog.id}`);
      toast.success('Investigación eliminada');
      setDeleteDialog({ open: false, id: null, titulo: '' });
      loadInvestigations();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleArchive = async (id) => {
    try {
      await api.post(`/investigations/${id}/archive`);
      toast.success('Investigación archivada');
      loadInvestigations();
    } catch (error) {
      toast.error('Error al archivar');
    }
  };

  const filteredInvestigations = investigations.filter(inv => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      inv.titulo?.toLowerCase().includes(term) ||
      inv.numero_investigacion?.toString().includes(term) ||
      inv.clientes?.some(c => c.toLowerCase().includes(term))
    );
  });

  return (
    <div className="p-4 lg:p-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileSearch className="text-purple-600" />
            Investigaciones
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Análisis forense de clientes y sus relaciones
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate('/m/monitoreo-transaccional/investigaciones/comparar')}
          className="mt-4 lg:mt-0 mr-2"
          data-testid="compare-investigations-btn"
        >
          <GitCompare size={16} className="mr-2" />
          Comparar
        </Button>
        <Button
          onClick={() => navigate('/m/monitoreo-transaccional/investigaciones/nueva')}
          style={{ backgroundColor: '#7c3aed' }}
          className="mt-4 lg:mt-0"
          data-testid="new-investigation-btn"
        >
          <Plus size={16} className="mr-2" />
          Nueva Investigación
        </Button>
      </div>

      {/* Filtros */}
      <Card className="p-4 mb-6 bg-white">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-slate-500 mb-1 block">Buscar</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por título, número o cliente..."
                className="pl-9"
                style={{ backgroundColor: '#fff' }}
              />
            </div>
          </div>
          <div className="w-48">
            <label className="text-xs text-slate-500 mb-1 block">Estado</label>
            <Select value={filtroEstado} onValueChange={setFiltroEstado}>
              <SelectTrigger style={{ backgroundColor: '#fff' }}>
                <SelectValue placeholder="Todos los estados" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="en_proceso">En Proceso</SelectItem>
                <SelectItem value="completada">Completada</SelectItem>
                <SelectItem value="archivada">Archivada</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={loadInvestigations} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'animate-spin mr-2' : 'mr-2'} />
            Actualizar
          </Button>
        </div>
      </Card>

      {/* Lista de Investigaciones */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw size={32} className="animate-spin text-purple-600" />
        </div>
      ) : filteredInvestigations.length === 0 ? (
        <Card className="p-12 bg-white text-center">
          <FileSearch size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-600 mb-2">
            No hay investigaciones
          </h3>
          <p className="text-sm text-slate-400 mb-4">
            {searchTerm ? 'No se encontraron resultados para la búsqueda' : 'Comienza creando una nueva investigación'}
          </p>
          <Button
            onClick={() => navigate('/m/monitoreo-transaccional/investigaciones/nueva')}
            style={{ backgroundColor: '#7c3aed' }}
          >
            <Plus size={16} className="mr-2" />
            Nueva Investigación
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredInvestigations.map((inv) => {
            const estadoConfig = ESTADO_CONFIG[inv.estado] || ESTADO_CONFIG.en_proceso;
            const EstadoIcon = estadoConfig.icon;
            const resultados = inv.resultados;
            
            return (
              <Card
                key={inv.id}
                className="p-4 bg-white hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/m/monitoreo-transaccional/investigaciones/${inv.id}`)}
                data-testid={`investigation-card-${inv.numero_investigacion}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  {/* Info Principal */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs font-mono bg-purple-100 text-purple-700 px-2 py-1 rounded">
                        #{inv.numero_investigacion}
                      </span>
                      <Badge className={estadoConfig.color}>
                        <EstadoIcon size={12} className="mr-1" />
                        {estadoConfig.label}
                      </Badge>
                      {inv.tipo_analisis === 'individual_relacionado' && (
                        <Badge className="bg-amber-100 text-amber-700">
                          <Users size={12} className="mr-1" />
                          Relacionado
                        </Badge>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      {inv.titulo}
                    </h3>
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Users size={14} />
                        {inv.clientes?.length || 0} cliente{inv.clientes?.length !== 1 ? 's' : ''}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {inv.fecha_desde} a {inv.fecha_hasta}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {formatDate(inv.created_at)}
                      </span>
                    </div>
                  </div>

                  {/* Métricas rápidas */}
                  {resultados?.resumen_global && (
                    <div className="flex gap-6 text-center">
                      <div>
                        <p className="text-xs text-slate-500">Transacciones</p>
                        <p className="text-lg font-bold text-slate-900">
                          {resultados.resumen_global.total_transacciones?.toLocaleString() || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Volumen</p>
                        <p className="text-lg font-bold text-purple-600">
                          {formatMoney(resultados.resumen_global.volumen_total)}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Acciones */}
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/m/monitoreo-transaccional/investigaciones/${inv.id}`)}
                    >
                      <Eye size={14} className="mr-1" />
                      Ver
                    </Button>
                    {inv.estado !== 'archivada' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleArchive(inv.id)}
                        title="Archivar investigación"
                      >
                        <Archive size={14} className="mr-1" />
                        Archivar
                      </Button>
                    )}
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setDeleteDialog({ open: true, id: inv.id, titulo: inv.titulo })}
                      title="Eliminar investigación"
                    >
                      <Trash2 size={14} className="mr-1" />
                      Eliminar
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog de confirmación de eliminación */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar investigación?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente la investigación 
              "{deleteDialog.titulo}" y todos sus resultados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InvestigacionesList;
